import { getCollection } from './firestoreService';
import { convertTimestamp } from './firestoreService';
import { subDays, format } from 'date-fns';

export interface AppCrash {
  id: string;
  message: string;
  stack_trace?: string;
  context?: string;
  user_id?: string;
  is_authenticated?: boolean;
  timestamp: Date;
  device_info?: { platform?: string; model?: string; version?: string };
  app_info?: { version?: string; build_number?: string };
  severity?: string;
  is_fatal?: boolean;
  environment?: string;
}

export interface CrashStatsDay {
  id: string;
  date: string;
  total_crashes: number;
  critical_count?: number;
  high_count?: number;
  last_updated?: Date;
}

export interface CrashAnalytics {
  crashes: AppCrash[];
  statsByDay: CrashStatsDay[];
  totalLast24h: number;
  totalLast7d: number;
  totalLast30d: number;
  bySeverity: Record<string, number>;
  byPlatform: Record<string, number>;
}

export const getCrashAnalytics = async (days: number = 30): Promise<CrashAnalytics> => {
  const startDate = subDays(new Date(), days);

  const [crashesRaw, statsRaw] = await Promise.all([
    getCollection<any>(
      'app_crashes',
      [{ field: 'timestamp', operator: '>=', value: startDate }],
      'timestamp',
      'desc',
      500
    ).catch(() => []),
    getCollection<any>(
      'crash_stats',
      [{ field: 'date', operator: '>=', value: format(startDate, 'yyyy-MM-dd') }],
      'date',
      'desc',
      90
    ).catch(() => []),
  ]);

  const crashes: AppCrash[] = crashesRaw.map((c: any) => ({
    id: c.id || '',
    message: c.message || '',
    stack_trace: c.stack_trace,
    context: c.context,
    user_id: c.user_id,
    is_authenticated: c.is_authenticated,
    timestamp: convertTimestamp(c.timestamp),
    device_info: c.device_info,
    app_info: c.app_info,
    severity: c.severity,
    is_fatal: c.is_fatal,
    environment: c.environment,
  }));

  const statsByDay: CrashStatsDay[] = statsRaw.map((s: any) => ({
    id: s.id || s.date,
    date: s.date || s.id,
    total_crashes: s.total_crashes || 0,
    critical_count: s.critical_count,
    high_count: s.high_count,
    last_updated: s.last_updated ? convertTimestamp(s.last_updated) : undefined,
  }));

  const now = new Date();
  const last24h = subDays(now, 1);
  const last7d = subDays(now, 7);
  const totalLast24h = crashes.filter((c) => c.timestamp >= last24h).length;
  const totalLast7d = crashes.filter((c) => c.timestamp >= last7d).length;
  const totalLast30d = crashes.length;

  const bySeverity: Record<string, number> = {};
  const byPlatform: Record<string, number> = {};
  crashes.forEach((c) => {
    const sev = c.severity || 'unknown';
    bySeverity[sev] = (bySeverity[sev] || 0) + 1;
    const platform = c.device_info?.platform || 'unknown';
    byPlatform[platform] = (byPlatform[platform] || 0) + 1;
  });

  return {
    crashes,
    statsByDay,
    totalLast24h,
    totalLast7d,
    totalLast30d,
    bySeverity,
    byPlatform,
  };
};
